import {
    AuthService,
    DataClientError,
    DataServerConditionNotification,
    DisplayableDataServerMessage,
    LocalConfigurationService,
    provideEntityModel,
    provideSearchModel,
    ValidationMessage
} from "@fedelta-pos/data-client";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {IdentityModel, LoginResult, LoginStatus, VerificationCodeDeliveryMethod} from "../identity.model";
import {ResponsiveService} from "../../responsive/responsive.service";
import {Local} from "../../configuration/config";
import {AppLoginAuthenticationMethod} from "../../configuration/app-entity.model";
import {navParam} from "../../navigation/navigation-params";
import {Page} from "../../navigation/page";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {IdentificationMode} from "./identification-mode";
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {DateFieldComponent} from '../../inputs/date-field/date-field.component';
import {DecimalFieldComponent} from '../../inputs/decimal-field/decimal-field.component';
import {EmailFieldComponent} from '../../inputs/email-field/email-field.component';
import {FieldPairComponent} from '../../inputs/form/field-pair/field-pair.component';
import {FormComponent} from '../../inputs/form/form.component';
import {PasswordFieldComponent} from '../../inputs/password-field/password-field.component';
import {PhoneNumberFieldComponent} from '../../inputs/phone-number-field/phone-number-field.component';
import {SelectorControlComponent} from '../../inputs/selector-control/selector-control.component';
import {TextFieldComponent} from '../../inputs/text-field/text-field.component';
import {IdentityRequestPasswordResetService} from "../identity-request-password-reset.service";
import {IdentityResetPasswordService} from "../identity-reset-password.service";
import {IdentityEntityModel} from "../identity-entity.model";
import {IdentitySearchData, IdentitySearchModel} from "../identity-search.model";
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {LegalPanelComponent} from '../../legal/legal-panel/legal-panel.component';
import {FormsModule} from '@angular/forms';
import {EmailDirective} from '../../inputs/email-field/email.directive';
import {PhoneNumberDirective} from '../../inputs/phone-number-field/phone-number.directive';
import {ButtonComponent} from '../../buttons/button/button.component';
import {LinkComponent} from '../../buttons/link/link.component';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {CustomerDetailsService} from '../../customer-info/customer-details.service';

@Component({
    selector: "woa-identification-page",
    templateUrl: "identification-page.component.html",
    styleUrl: "./identification-page.component.scss",
    providers: [
        provideSearchModel(IdentitySearchModel),
        provideEntityModel(IdentityEntityModel),
        IdentityRequestPasswordResetService,
        IdentityResetPasswordService,
        {provide: Page, useExisting: IdentificationPageComponent},
    ],
    imports: [
        ButtonComponent,
        ContentMessageComponent,
        DateFieldComponent,
        DecimalFieldComponent,
        EmailDirective,
        EmailFieldComponent,
        FieldPairComponent,
        FormComponent,
        FormsModule,
        LegalPanelComponent,
        LinkComponent,
        ModalPageLayoutComponent,
        PageFooterControlDirective,
        PasswordFieldComponent,
        PhoneNumberDirective,
        PhoneNumberFieldComponent,
        SelectorControlComponent,
        TextFieldComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class IdentificationPageComponent extends Page implements AfterViewInit {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public identityModel: IdentityModel = inject(IdentityModel);
    public identityEntityModel: IdentityEntityModel = inject(IdentityEntityModel);
    public loadingService: LoadingService = inject(LoadingService);
    private customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);
    private identitySearchModel: IdentitySearchModel = inject(IdentitySearchModel);
    private localConfigurationService: LocalConfigurationService = inject(LocalConfigurationService);
    private requestPasswordResetService: IdentityRequestPasswordResetService = inject(IdentityRequestPasswordResetService);
    private resetPasswordService: IdentityResetPasswordService = inject(IdentityResetPasswordService);
    private authService: AuthService = inject(AuthService);
    private responsiveService: ResponsiveService = inject(ResponsiveService);

    IdentificationMode = IdentificationMode;
    AppLoginAuthenticationMethod = AppLoginAuthenticationMethod;
    MessageLevel = MessageLevel;

    public readonly mode: WritableSignal<IdentificationMode | undefined> = navParam.required<IdentificationMode>("mode");

    public readonly form: Signal<FormComponent | undefined> = viewChild(FormComponent);

    public readonly loginErrorKey: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public readonly loginErrorParams: WritableSignal<Record<string, string | number>> = signal<Record<string, string | number>>(undefined);

    public readonly primaryIdentity: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public readonly identityPassword: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public readonly secondaryMobilePhone: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public readonly remember: WritableSignal<boolean> = signal<boolean>(this.responsiveService.isMobile());
    public readonly registeredIdentity: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public readonly fullIdentity: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public readonly verificationCode: WritableSignal<string | undefined> = signal<string | undefined>(undefined);

    public readonly usedThisApp: WritableSignal<boolean> = signal<boolean>(false);
    public readonly usedOtherApp: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public readonly accountHasNoPassword: WritableSignal<boolean> = signal<boolean>(false);
    public readonly checkRegistrationFailed: WritableSignal<boolean> = signal<boolean>(false);
    public readonly registrationFailed: WritableSignal<boolean> = signal<boolean>(false);
    public readonly confirmationFailed: WritableSignal<boolean> = signal<boolean>(false);
    public readonly verificationId: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public readonly verificationCodeDeliveryMethod: WritableSignal<VerificationCodeDeliveryMethod | undefined> = signal<VerificationCodeDeliveryMethod | undefined>(undefined);
    public readonly verificationCodeDeliveryDetails: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
    public readonly verificationFailed: WritableSignal<boolean> = signal<boolean>(false);
    public readonly verificationCodeResent: WritableSignal<boolean> = signal<boolean>(false);
    public readonly requestPasswordResetFailed: WritableSignal<boolean> = signal<boolean>(false);
    public readonly passwordResetFailed: WritableSignal<boolean> = signal<boolean>(false);
    public readonly passwordResetCodeResent: WritableSignal<boolean> = signal<boolean>(false);

    // Computed signals
    public readonly title: Signal<string | undefined> = computed<string | undefined>(() =>
        this.mode() === IdentificationMode.LOGIN ? "woa.heading.identification.loginTitle"
            : this.mode() === IdentificationMode.VERIFY_LOGIN ? "woa.heading.identification.verifyLoginTitle"
                : this.mode() === IdentificationMode.REGISTER || this.mode() === IdentificationMode.REGISTRATION_DETAILS ? "woa.heading.identification.registerTitle"
                    : this.mode() === IdentificationMode.CONFIRM ? "woa.heading.identification.confirmTitle"
                        : this.mode() === IdentificationMode.VERIFY_REGISTRATION ? "woa.heading.identification.verifyRegistrationTitle"
                            : this.mode() === IdentificationMode.ENABLE_ACCOUNT ? "woa.heading.identification.enableAccountTitle"
                                : this.mode() === IdentificationMode.FORGOT_PASSWORD ? "woa.heading.identification.forgotPasswordTitle"
                                    : this.mode() === IdentificationMode.SET_PASSWORD ? "woa.heading.identification.setPasswordTitle"
                                        : this.mode() === IdentificationMode.RESET_PASSWORD ? "woa.heading.identification.resetPasswordTitle"
                                            : undefined
    );

    public readonly subtitle: Signal<string | undefined> = computed<string | undefined>(() =>
        this.mode() === IdentificationMode.CONFIRM ? "woa.heading.identification.confirmSubtitle"
            : undefined
    );

    public readonly contentText: Signal<string | undefined> = computed<string | undefined>(() =>
        (this.mode() === IdentificationMode.ENABLE_ACCOUNT || this.mode() === IdentificationMode.FORGOT_PASSWORD) && !this.requestPasswordResetFailed()
            ? this.mode() === IdentificationMode.ENABLE_ACCOUNT && this.primaryIdentity()
                ? "woa.message.identification.enable-account.knownIdentity"
                : this.configurationModel.loginIdentityIsEmail()
                    ? this.mode() === IdentificationMode.FORGOT_PASSWORD
                        ? "woa.message.identification.forgot-password.email"
                        : "woa.message.identification.enable-account.unknownIdentity.email"
                    : this.configurationModel.loginIdentityIsMobile()
                        ? this.mode() === IdentificationMode.FORGOT_PASSWORD
                            ? "woa.message.identification.forgot-password.mobile"
                            : "woa.message.identification.enable-account.unknownIdentity.mobile"
                        : this.configurationModel.loginIdentityIsMemberNo()
                            ? this.mode() === IdentificationMode.FORGOT_PASSWORD
                                ? "woa.message.identification.forgot-password.member-no"
                                : "woa.message.identification.enable-account.unknownIdentity.member-no"
                            : undefined
            : (this.mode() === IdentificationMode.VERIFY_LOGIN || this.mode() === IdentificationMode.VERIFY_REGISTRATION) && this.verificationId() && !this.verificationFailed()
                ? this.verificationCodeDeliveryMethod() === VerificationCodeDeliveryMethod.EMAIL
                    ? this.verificationCodeResent() ? "woa.message.identification.verificationCodeResentEmail" : "woa.message.identification.verificationCodeSentEmail"
                    : this.verificationCodeResent() ? "woa.message.identification.verificationCodeResentMobile" : "woa.message.identification.verificationCodeSentMobile"
                : (this.mode() === IdentificationMode.SET_PASSWORD || this.mode() === IdentificationMode.RESET_PASSWORD) && !this.passwordResetFailed()
                    ? this.passwordResetCodeResent() ? "woa.message.identification.passwordResetCodeResent" : "woa.message.identification.passwordResetCodeSent"
                    : undefined
    );

    public readonly alreadyRegisteredMessageText: Signal<string> = computed<string>(() =>
        this.usedThisApp() ? (
                this.configurationModel.loginIdentityIsEmail() ? "woa.message.identification.thisAppUsedEmail"
                    : this.configurationModel.loginIdentityIsMobile() ? "woa.message.identification.thisAppUsedMobile"
                        : "woa.message.identification.thisAppUsed"
            )
            : this.usedOtherApp() ? "woa.message.identification.otherAppUsed"
                : "woa.message.identification.noAppUsed"
    );

    public readonly pageLayoutTranslationParams: Signal<Record<string, string | number>> = computed<Record<string, string | number>>(() => ({
        "identity": this.primaryIdentity(),
        "verificationCodeDeliveryDetails": this.verificationCodeDeliveryDetails()
    }));

    public readonly submitButtonLabel: Signal<string | undefined> = computed<string | undefined>(() =>
        this.mode() === IdentificationMode.LOGIN ? "woa.button.identification.login"
            : this.mode() === IdentificationMode.VERIFY_LOGIN ? "woa.button.identification.verify"
                : this.mode() === IdentificationMode.REGISTER ? "woa.button.identification.continue"
                    : this.mode() === IdentificationMode.REGISTRATION_DETAILS ? "woa.button.identification.register"
                        : this.mode() === IdentificationMode.VERIFY_REGISTRATION ? "woa.button.identification.verify"
                            : this.mode() === IdentificationMode.ENABLE_ACCOUNT || this.mode() === IdentificationMode.FORGOT_PASSWORD ? "woa.button.identification.sendCode"
                                : this.mode() === IdentificationMode.SET_PASSWORD ? "woa.button.identification.setPassword"
                                    : this.mode() === IdentificationMode.RESET_PASSWORD ? "woa.button.identification.changePassword"
                                        : undefined
    );

    public readonly legalMessageSet: Signal<string | undefined> = computed<string | undefined>(() =>
        this.mode() === IdentificationMode.LOGIN || this.mode() === IdentificationMode.VERIFY_LOGIN ? "identification.login"
            : this.mode() === IdentificationMode.REGISTER || this.mode() === IdentificationMode.REGISTRATION_DETAILS ? "identification.register"
                : undefined
    );

    private appId: number;

    constructor() {
        super();

        (async () => this.appId = await this.localConfigurationService.getNumber(Local.APP_ID))();
        this.identityEntityModel.newEntity();
    }

    async ngAfterViewInit(): Promise<void> {
        if (!this.authService.isLoggedIn()) {
            await this.identityModel.loginAsGuest();
        }
        if (this.primaryIdentity() && this.identityPassword() && !this.form()?.submitted()) {
            void this.login();
        }
    }

    get email(): string {
        return this.mode() === IdentificationMode.REGISTRATION_DETAILS ? this.identityEntityModel.dataSignal()?.email : this.primaryIdentity();
    }

    set email(email: string) {
        if (this.mode() === IdentificationMode.REGISTRATION_DETAILS) {
            this.identityEntityModel.data.email = email?.toLowerCase();
        } else {
            this.primaryIdentity.set(email?.toLowerCase());
        }
    }

    public resetLoginDetails(): void {
        this.primaryIdentity.set(undefined);
        this.identityPassword.set(undefined);
        this.secondaryMobilePhone.set(undefined);
        this.remember.set(this.responsiveService.isMobile());
        this.form()?.reset();
    }

    public async login(): Promise<void> {
        if (!this.form()?.valid) {
            return;
        }

        this.registeredIdentity.set(undefined);
        this.accountHasNoPassword.set(false);

        void this.loadingService.performWithSpinner(async () => {
            this.fullIdentity.set(this.primaryIdentity());
            if (this.configurationModel.loginRequiresSecondaryMobile()) {
                this.fullIdentity.set(this.fullIdentity() + ";" + this.secondaryMobilePhone());
            }
            const loginResult: LoginResult = await this.identityModel.loginAsMember(this.configurationModel.loginType(), this.remember(), this.fullIdentity(), this.identityPassword());
            if (loginResult.status === LoginStatus.SUCCESS) {
                void this.dismiss(true);
            } else if (loginResult.status === LoginStatus.IDENTITY_VERIFICATION_REQUIRED) {
                this.verificationId.set(loginResult.verificationId);
                this.verificationCodeDeliveryMethod.set(loginResult.verificationCodeDeliveryMethod);
                this.verificationCodeDeliveryDetails.set(loginResult.verificationCodeDeliveryDetails);
                this.mode.set(IdentificationMode.VERIFY_LOGIN);
            } else if (loginResult.status === LoginStatus.IDENTITY_HAS_NO_PASSWORD) {
                if (this.configurationModel.supportsForgotPassword()) {
                    this.mode.set(IdentificationMode.ENABLE_ACCOUNT);
                } else {
                    this.accountHasNoPassword.set(true);
                }
            } else {
                const displayableMessage: DisplayableDataServerMessage = loginResult.error instanceof DataClientError ? await loginResult.error.displayableMessage : undefined;

                if (loginResult.error?.status === 401) {
                    if (displayableMessage?.messageKey) {
                        this.loginErrorKey.set(displayableMessage.messageKey);
                        this.loginErrorParams.set(displayableMessage.messageParams);
                    } else {
                        this.loginErrorKey.set("woa.error.identification.loginFailed");
                        this.loginErrorParams.set({});
                    }
                }
                this.resetLoginDetails();
            }
        });
    }

    public verifyLogin(): void {
        if (!this.form()?.valid) {
            return;
        }

        this.verificationFailed.set(false);
        void this.loadingService.performWithSpinner(async () => {
            const loginResult: LoginResult = await this.identityModel.loginAsMember(this.configurationModel.loginType(), this.remember(), this.fullIdentity(), this.identityPassword(), this.verificationId(), this.verificationCode());
            if (loginResult.status === LoginStatus.SUCCESS) {
                if (this.remember()) {
                    this.customerDetailsService.saveLogin(this.primaryIdentity(), loginResult.authKey);
                }
                void this.dismiss(true);
            } else {
                this.verificationFailed.set(true);
                this.verificationCode.set(undefined);
            }
            this.form()?.markAsUnsubmitted();
        });
    }

    public resendLoginVerificationCode(): void {
        this.form()?.markAsUnsubmitted();
        this.verificationFailed.set(false);
        this.verificationCode.set(undefined);

        void this.loadingService.performWithSpinner(async () => {
            const loginResult: LoginResult = await this.identityModel.loginAsMember(this.configurationModel.loginType(), this.remember(), this.fullIdentity(), this.identityPassword());
            if (loginResult.status === LoginStatus.SUCCESS) { // Unexpected at this point
                void this.dismiss(true);
            } else if (loginResult.status === LoginStatus.IDENTITY_VERIFICATION_REQUIRED) {
                this.verificationId.set(loginResult.verificationId);
                this.verificationCodeDeliveryMethod.set(loginResult.verificationCodeDeliveryMethod);
                this.verificationCodeDeliveryDetails.set(loginResult.verificationCodeDeliveryDetails);
                this.verificationCodeResent.set(true);
            } else {
                this.verificationFailed.set(true);
            }
        });
    }

    public checkRegistration(): void {
        if (!this.form()?.valid) {
            return;
        }

        this.registeredIdentity.set(undefined);
        this.accountHasNoPassword.set(false);
        this.usedThisApp.set(false);
        this.usedOtherApp.set(undefined);
        this.checkRegistrationFailed.set(false);

        void this.loadingService.performWithSpinner(async () => {
            try {
                await this.identitySearchModel.retrieveByIdentity(this.primaryIdentity(), this.configurationModel.loginIdentificationMethod(), 5000);
                if (this.identitySearchModel.total === 0) {
                    this.identityEntityModel.newEntity();
                    if (this.configurationModel.loginIdentityIsEmail()) {
                        this.identityEntityModel.data.email = this.primaryIdentity();
                    }
                    if (this.configurationModel.loginIdentityIsMobile()) {
                        this.identityEntityModel.data.mobilePhone = this.primaryIdentity();
                    }
                    this.mode.set(IdentificationMode.REGISTRATION_DETAILS);
                } else {
                    this.identitySearchModel.dataSignal()?.some((identitySearchData: IdentitySearchData) => {
                        if (!identitySearchData.passwordSet && this.configurationModel.loginAuthenticationMethod() === AppLoginAuthenticationMethod.PASSWORD) {
                            if (this.configurationModel.supportsForgotPassword()) {
                                this.mode.set(IdentificationMode.ENABLE_ACCOUNT);
                            } else {
                                this.accountHasNoPassword.set(true);
                            }
                            return true;
                        }

                        this.registeredIdentity.set(this.primaryIdentity());
                        if (identitySearchData.appId === this.appId) {
                            this.usedThisApp.set(true);
                            this.mode.set(IdentificationMode.LOGIN);
                            return true;
                        } else if (identitySearchData.appName && !this.usedOtherApp()) {
                            this.usedOtherApp.set(identitySearchData.appName);
                        }
                        this.mode.set(IdentificationMode.LOGIN);
                        return false;
                    });
                }
            } catch (error) {
                console.error(error);
                this.checkRegistrationFailed.set(true);
            }
        });
    }

    public register(): void {
        if (!this.form()?.valid) {
            return;
        }

        const password: string = this.identityEntityModel.dataSignal()?.password;
        this.registrationFailed.set(false);
        void this.loadingService.performWithSpinner(async () => {
            try {
                const validationMessages: Array<ValidationMessage> = await this.identityEntityModel.saveEntity();
                this.identityEntityModel.data.password = password;
                if (validationMessages) {
                    this.registrationFailed.set(true);
                } else {
                    void this.confirmLogin();
                }
            } catch (error) {
                const conditionNotification: DataServerConditionNotification = error instanceof DataClientError ? await error.conditionNotification : undefined;
                if (error.status === 403 && conditionNotification?.conditionName === "register.registration2FARequired") {
                    this.verificationId.set(conditionNotification.conditionParams["verificationId"]);
                    this.verificationCodeDeliveryMethod.set(conditionNotification.conditionParams["verificationCodeDeliveryMethod"]);
                    this.verificationCodeDeliveryDetails.set(conditionNotification.conditionParams["verificationCodeDeliveryDetails"]);
                    this.mode.set(IdentificationMode.VERIFY_REGISTRATION);
                    return;
                }
                console.error(error);
                this.registrationFailed.set(true);
            }
        });
    }

    public verifyRegistration(): void {
        if (!this.form()?.valid) {
            return;
        }

        this.verificationFailed.set(false);
        this.identityEntityModel.data.verificationId = this.verificationId();
        this.identityEntityModel.data.verificationCode = this.verificationCode();
        void this.loadingService.performWithSpinner(async () => {
            try {
                const validationMessages: Array<ValidationMessage> = await this.identityEntityModel.saveEntity();
                if (validationMessages) {
                    this.verificationFailed.set(true);
                } else {
                    const loginResult: LoginResult = await this.confirmLogin();
                    if (loginResult.status === LoginStatus.SUCCESS && this.remember()) {
                        this.customerDetailsService.saveLogin(this.primaryIdentity(), loginResult.authKey);
                    }
                }
            } catch (error) {
                console.error(error);
                this.verificationFailed.set(true);
            } finally {
                this.verificationId.set(undefined);
                this.verificationCodeDeliveryMethod.set(undefined);
                this.verificationCode.set(undefined);
            }
        });
    }

    public resendRegistrationVerificationCode(): void {
        this.form()?.markAsUnsubmitted();
        this.verificationFailed.set(false);
        this.verificationCode.set(undefined);

        void this.loadingService.performWithSpinner(async () => {
            try {
                await this.identityEntityModel.saveEntity();

                // Unexpected at this point
                void this.dismiss(true);
            } catch (error) {
                const conditionNotification: DataServerConditionNotification = error instanceof DataClientError ? await error.conditionNotification : undefined;
                if (error.status === 403 && conditionNotification?.conditionName === "register.registration2FARequired") {
                    this.verificationId.set(conditionNotification.conditionParams["verificationId"]);
                    this.verificationCodeDeliveryMethod.set(conditionNotification.conditionParams["verificationCodeDeliveryMethod"]);
                    this.verificationCodeDeliveryDetails.set(conditionNotification.conditionParams["verificationCodeDeliveryDetails"]);
                    this.verificationCodeResent.set(true);
                    return;
                } else {
                    console.error(error);
                    this.verificationFailed.set(true);
                }
            }
        });
    }

    public requestPasswordReset(): void {
        if (!this.form()?.valid) {
            return;
        }

        this.requestPasswordResetFailed.set(false);
        void this.loadingService.performWithSpinner(async () => {
            try {
                await this.requestPasswordResetService.performAction({
                    loginType: this.configurationModel.loginType(),
                    identity: this.primaryIdentity()
                });
                this.mode.set(this.mode() === IdentificationMode.ENABLE_ACCOUNT ? IdentificationMode.SET_PASSWORD : IdentificationMode.RESET_PASSWORD);
            } catch (error) {
                console.error(error);
                this.requestPasswordResetFailed.set(true);
            }
        });
    }

    public resetPassword(): void {
        if (!this.form()?.valid) {
            return;
        }

        this.passwordResetFailed.set(false);
        void this.loadingService.performWithSpinner(async () => {
            try {
                await this.resetPasswordService.performAction({
                    loginType: this.configurationModel.loginType(),
                    identity: this.primaryIdentity(),
                    code: this.verificationCode(),
                    password: this.identityEntityModel.dataSignal()?.password
                });
                const loginResult: LoginResult = await this.confirmLogin();
                if (loginResult.status === LoginStatus.SUCCESS && this.remember()) {
                    this.customerDetailsService.saveLogin(this.primaryIdentity(), loginResult.authKey);
                }
            } catch (error) {
                console.error(error);
                this.passwordResetFailed.set(true);
            }
        });
    }

    public resendPasswordResetCode(): void {
        this.form()?.markAsUnsubmitted();
        this.passwordResetFailed.set(false);
        this.verificationCode.set(undefined);

        void this.loadingService.performWithSpinner(async () => {
            try {
                await this.requestPasswordResetService.performAction({
                    loginType: this.configurationModel.loginType(),
                    identity: this.primaryIdentity()
                });
                this.passwordResetCodeResent.set(true);
            } catch (error) {
                console.error(error);
                this.requestPasswordResetFailed.set(true);
            }
        });
    }

    public async confirmLogin(): Promise<LoginResult> {
        this.mode.set(IdentificationMode.CONFIRM);
        this.confirmationFailed.set(false);
        return this.loadingService.performWithSpinner(async () => {

            // The delay is an attempt to allow signup deals to sync to the App before showing the main member screen
            await new Promise((resolve) => setTimeout(resolve, this.configurationModel.app()?.signupDelayMillis || 3000));

            const loginResult: LoginResult = await this.identityModel.loginAsMember(this.configurationModel.loginType(), !!this.remember(), this.primaryIdentity(), this.identityEntityModel.dataSignal()?.password, this.identityEntityModel.dataSignal()?.verificationId, this.identityEntityModel.dataSignal()?.verificationCode);
            if (loginResult.status === LoginStatus.SUCCESS) {
                void this.dismiss(true);
            } else {
                this.confirmationFailed.set(true);
            }
            return loginResult;
        });
    }
}
